import React, { useState } from 'react';
import { 
  X, Phone, MessageCircle, Calendar, DollarSign, Building2, 
  Mail, User, Clock, Flame, Thermometer, Snowflake, ChevronDown,
  CheckCircle, ArrowRightLeft, MessageSquare, AlertCircle, Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/utils/api';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/helpers';

const STAGE_LABELS = {
  lead: 'Lead',
  primeiro_contato: 'Primeiro Contato',
  proposta_enviada: 'Proposta Enviada',
  negociacao: 'Negociação',
  fechado: 'Fechado',
  perdido: 'Perdido',
};

const STAGE_COLORS = {
  lead: 'bg-slate-100 text-slate-700',
  primeiro_contato: 'bg-blue-100 text-blue-700',
  proposta_enviada: 'bg-yellow-100 text-yellow-700',
  negociacao: 'bg-purple-100 text-purple-700',
  fechado: 'bg-green-100 text-green-700',
  perdido: 'bg-red-100 text-red-700',
};

const TEMPERATURA_CONFIG = {
  quente: { label: 'Quente', icon: Flame, color: 'text-red-500', bg: 'bg-red-50 border-red-200' },
  morno:  { label: 'Morno',  icon: Thermometer, color: 'text-amber-500', bg: 'bg-amber-50 border-amber-200' },
  frio:   { label: 'Frio',   icon: Snowflake, color: 'text-blue-500', bg: 'bg-blue-50 border-blue-200' },
};

const ACTIVITY_ICONS = {
  criacao: { icon: Plus, color: 'text-slate-500', bg: 'bg-slate-100' },
  mudanca_etapa: { icon: ArrowRightLeft, color: 'text-blue-500', bg: 'bg-blue-100' },
  observacao: { icon: MessageSquare, color: 'text-violet-500', bg: 'bg-violet-100' },
  contato: { icon: Phone, color: 'text-green-500', bg: 'bg-green-100' },
  proposta: { icon: DollarSign, color: 'text-amber-500', bg: 'bg-amber-100' },
};

function openWhatsApp(phone, name = '') {
  if (!phone) { toast.error('Lead sem telefone cadastrado'); return; }
  const cleaned = phone.replace(/\D/g, '');
  const number = cleaned.startsWith('55') ? cleaned : `55${cleaned}`;
  window.open(`https://wa.me/${number}`, '_blank');
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

function formatRelDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return 'Ontem';
  if (diffDays < 7) return `${diffDays}d atrás`;
  return d.toLocaleDateString('pt-BR');
}

export default function LeadDetailModal({ lead, onClose, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [editValues, setEditValues] = useState({
    temperatura: lead?.temperatura || 'morno',
    valor_estimado: lead?.valor_estimado || 0,
    proxima_acao_descricao: lead?.proxima_acao_descricao || '',
    proxima_acao: lead?.proxima_acao ? new Date(lead.proxima_acao).toISOString().split('T')[0] : '',
  });

  if (!lead) return null;

  const activities = [...(lead.activities || [])].reverse();
  const overdue = isOverdue(lead.proxima_acao);

  const handleSave = async (field, value) => {
    setSaving(true);
    try {
      const payload = { [field]: value };
      await api.put(`/leads/${lead.id}`, payload);
      onUpdate?.();
      toast.success('Atualizado!');
    } catch (e) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveStage = async (newStatus) => {
    setSaving(true);
    try {
      await api.put(`/leads/${lead.id}`, { status: newStatus });
      onUpdate?.();
      toast.success(`Movido para ${STAGE_LABELS[newStatus]}`);
      onClose();
    } catch (e) {
      toast.error('Erro ao mover lead');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/leads/${lead.id}/activity`, {
        tipo: 'observacao',
        descricao: newNote.trim(),
      });
      setNewNote('');
      setAddingNote(false);
      onUpdate?.();
      toast.success('Observação adicionada!');
    } catch (e) {
      toast.error('Erro ao adicionar observação');
    }
  };

  const TempIcon = TEMPERATURA_CONFIG[editValues.temperatura]?.icon || Thermometer;

  const STAGES_ORDER = ['lead', 'primeiro_contato', 'proposta_enviada', 'negociacao', 'fechado', 'perdido'];

  return (
    <Dialog open={!!lead} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto premium-scrollbar p-0">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-slate-900 truncate">{lead.title}</h2>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STAGE_COLORS[lead.status]}`}>
                  {STAGE_LABELS[lead.status]}
                </span>
                {lead.origem && (
                  <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{lead.origem}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {lead.contact_phone && (
                <Button size="sm" variant="outline" className="gap-1.5 text-green-700 border-green-200 hover:bg-green-50"
                  onClick={() => openWhatsApp(lead.contact_phone, lead.contact_name)}>
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </Button>
              )}
              {lead.contact_phone && (
                <Button size="sm" variant="outline" className="gap-1.5"
                  onClick={() => window.location.href = `tel:${lead.contact_phone}`}>
                  <Phone className="h-3.5 w-3.5" /> Ligar
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Dados de Contato */}
          <div className="grid grid-cols-2 gap-3">
            {lead.contact_name && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <span>{lead.contact_name}</span>
              </div>
            )}
            {lead.company_name && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <span>{lead.company_name}</span>
              </div>
            )}
            {lead.contact_email && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Mail className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <span className="truncate">{lead.contact_email}</span>
              </div>
            )}
            {lead.contact_phone && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Phone className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <span>{lead.contact_phone}</span>
              </div>
            )}
          </div>

          {/* Temperatura + Valor */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Temperatura</Label>
              <div className="flex gap-1.5">
                {Object.entries(TEMPERATURA_CONFIG).map(([key, cfg]) => {
                  const Icon = cfg.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        setEditValues(v => ({ ...v, temperatura: key }));
                        handleSave('temperatura', key);
                      }}
                      className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                        editValues.temperatura === key ? cfg.bg : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'
                      }`}
                    >
                      <Icon className={`h-3.5 w-3.5 ${editValues.temperatura === key ? cfg.color : ''}`} />
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Valor Estimado</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={editValues.valor_estimado}
                  onChange={e => setEditValues(v => ({ ...v, valor_estimado: e.target.value }))}
                  onBlur={() => handleSave('valor_estimado', parseFloat(editValues.valor_estimado) || 0)}
                  className="h-9 text-sm"
                  placeholder="0,00"
                />
              </div>
            </div>
          </div>

          {/* Próxima Ação */}
          <div>
            <Label className="text-xs text-slate-500 mb-1.5 block flex items-center gap-1.5">
              Próxima Ação
              {overdue && <span className="text-red-500 text-[10px] bg-red-50 px-1.5 py-0.5 rounded font-bold">ATRASADA</span>}
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Ex: Ligar para confirmar proposta"
                value={editValues.proxima_acao_descricao}
                onChange={e => setEditValues(v => ({ ...v, proxima_acao_descricao: e.target.value }))}
                onBlur={() => handleSave('proxima_acao_descricao', editValues.proxima_acao_descricao)}
                className={`h-9 text-sm ${overdue ? 'border-red-300 bg-red-50 focus:border-red-400' : ''}`}
              />
              <Input
                type="date"
                value={editValues.proxima_acao}
                onChange={e => setEditValues(v => ({ ...v, proxima_acao: e.target.value }))}
                onBlur={() => {
                  if (editValues.proxima_acao) {
                    handleSave('proxima_acao', new Date(editValues.proxima_acao).toISOString());
                  }
                }}
                className={`h-9 text-sm ${overdue ? 'border-red-300 bg-red-50' : ''}`}
              />
            </div>
          </div>

          {/* Mover Etapa */}
          <div>
            <Label className="text-xs text-slate-500 mb-2 block">Mover para Etapa</Label>
            <div className="flex flex-wrap gap-1.5">
              {STAGES_ORDER.filter(s => s !== lead.status).map(stage => (
                <button
                  key={stage}
                  onClick={() => handleMoveStage(stage)}
                  className={`text-xs px-3 py-1.5 rounded-lg font-medium border transition-all hover:scale-105 ${STAGE_COLORS[stage]} border-current/20`}
                >
                  → {STAGE_LABELS[stage]}
                </button>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs text-slate-500">Histórico</Label>
              <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-violet-600 hover:bg-violet-50"
                onClick={() => setAddingNote(!addingNote)}>
                <Plus className="h-3 w-3" /> Nova observação
              </Button>
            </div>

            {addingNote && (
              <div className="mb-3 flex gap-2">
                <Input
                  autoFocus
                  placeholder="Descreva o contato, observação ou próximo passo..."
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddNote()}
                  className="h-9 text-sm flex-1"
                />
                <Button size="sm" onClick={handleAddNote}>Salvar</Button>
                <Button size="sm" variant="ghost" onClick={() => { setAddingNote(false); setNewNote(''); }}>✕</Button>
              </div>
            )}

            {activities.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">Nenhuma atividade registrada</p>
            ) : (
              <div className="space-y-2 max-h-52 overflow-y-auto premium-scrollbar pr-1">
                {activities.map((act, idx) => {
                  const config = ACTIVITY_ICONS[act.tipo] || ACTIVITY_ICONS.observacao;
                  const Icon = config.icon;
                  return (
                    <div key={act.id || idx} className="flex items-start gap-2.5">
                      <div className={`h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${config.bg}`}>
                        <Icon className={`h-3 w-3 ${config.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-700 leading-relaxed">{act.descricao}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {act.user_name} · {formatRelDate(act.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
